import { html, LitElement } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../utils/translator.helper';
import { GzDialogEvents } from '../../../../ui/gzDialog/component/GzDialog';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { FormBuildConfig } from '../../../../ui/gzForm/formGroup/types/form-build-config';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { Subscription } from 'rxjs';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { ButtonColors, ButtonTypes } from '../../../../ui/gzButton/types';
import { FontSizes } from '../../../../types/common/types';
import { FormValidator } from '../../../../ui/gzForm/formField/types/form-validator';

/**
 * @summary "Save as" dialog content
 * @tag gz-dashboard-save-as-dialog-content
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-dashboard-save-as-dialog-content')
export class GzDashboardSaveAsDialogContentWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  /**
   * Array of view name validators
   * @type {Array<any>}
   */
  @property({
    attribute: false,
    reflect: false,
    type: Array,
  })
  public validators: Array<FormValidator>;

  /**
   * @internal
   * @type {FormGroup}
   * @private
   */
  private formGroup: FormGroup;

  /**
   * @internal
   * @type {GzInputWebComponent}
   * @private
   */
  @query('#saveViewInput')
  private inputComponent!: GzInputWebComponent;

  /**
   * @internal
   * @type {GzFormFieldWebComponent}
   * @private
   */
  @query('#nameInput')
  private nameInput!: GzFormFieldWebComponent;

  /**
   * @internal
   * @type {GzButtonWebComponent}
   * @private
   */
  @query('#saveButton')
  private saveButton: GzButtonWebComponent;

  /**
   * @internal
   * @type {Subscription | null}
   * @private
   */
  @state()
  private subscription: Subscription | null;

  public firstUpdated() {
    this.nameInput.Validators = this.validators;

    const config: FormBuildConfig = {
      name: this.nameInput,
    };

    this.formGroup = FormGroup.build(config);

    this.subscription = this.formGroup.valid$.subscribe({
      next: valid => {
        this.saveButton.disabled = !valid;
      },
    });

    setTimeout(() => {
      this.inputComponent.inputFocus();
    }, 100);
  }

  render() {
    return html`
      <gz-text fontSize="${FontSizes.HEADING_6}"
        >${translateFn('This will save the current view as a new entry in the Custom section.')}
      </gz-text>

      <gz-form-field label="${translateFn('Name')}" id="nameInput" class="pt-2 pb-3">
        <gz-input
          maxLength="64"
          gz-form-input
          type="text"
          id="saveViewInput"
          placeholder="${translateFn('Type here')}"
        ></gz-input>
        <gz-input-error id="inputError"></gz-input-error>
      </gz-form-field>

      <div class="action-buttons">
        <gz-button type="${ButtonTypes.FULL}" id="saveButton" class="mr-2" @click="${this.handleSaveButton}">
          ${translateFn('Save#preserve state')}
        </gz-button>
        <gz-button type="${ButtonTypes.OUTLINE}" color="${ButtonColors.SECONDARY}" @click="${this.handleCancelButton}">
          ${translateFn('Cancel')}
        </gz-button>
      </div>
    `;
  }

  private handleCancelButton(): void {
    this.dispatchEvent(
      new CustomEvent(GzDialogEvents.CANCEL, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleSaveButton(): void {
    this.dispatchEvent(
      new CustomEvent(GzDialogEvents.CONFIRM, {
        bubbles: true,
        composed: true,
        detail: {
          viewName: this.formGroup.value.name,
        },
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-dashboard-save-as-dialog-content': GzDashboardSaveAsDialogContentWebComponent;
  }
}
